import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Field, Input, Textarea } from "./adminUi";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import { iconNames } from "../../components/ServiceIcon";
import ServiceIcon from "../../components/ServiceIcon";

const EMPTY = { title: "", tagline: "", description: "", icon: "Sparkles", features: [], order: 0 };

export default function AdminServices() {
  const [services, setServices] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/services").then((r) => setServices(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing("new"); setForm({ ...EMPTY, order: services.length + 1 }); };
  const openEdit = (s) => { setEditing(s.id); setForm({ title: s.title, tagline: s.tagline, description: s.description, icon: s.icon, features: s.features || [], order: s.order }); };
  const close = () => { setEditing(null); setForm(EMPTY); };

  const save = async () => {
    setSaving(true);
    try {
      if (editing === "new") await api.post("/admin/services", form);
      else await api.put(`/admin/services/${editing}`, form);
      toast.success("Saved");
      close();
      await load();
    } catch { toast.error("Save failed"); } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this service?")) return;
    try { await api.delete(`/admin/services/${id}`); toast.success("Deleted"); await load(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div data-testid="admin-services">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-fuchsia-400">Services</p>
          <h1 className="mt-2 text-3xl font-bold tracking-tight text-white md:text-4xl">Manage services</h1>
        </div>
        <button onClick={openNew} data-testid="services-add-btn" className="group relative inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-xs font-semibold text-white">
          <span className="absolute inset-0 rounded-full gradient-bg" />
          <span className="relative flex items-center gap-2"><Plus size={14} /> Add service</span>
        </button>
      </div>

      <div className="mt-8 overflow-hidden rounded-2xl border border-white/10 bg-[#131B2F]">
        <table className="w-full text-left">
          <thead className="bg-white/5 text-xs uppercase tracking-[0.15em] text-white/50">
            <tr><th className="p-4">Service</th><th className="p-4">Tagline</th><th className="p-4">Order</th><th className="p-4 text-right">Actions</th></tr>
          </thead>
          <tbody>
            {services.map((s) => (
              <tr key={s.id} className="border-t border-white/5 text-sm">
                <td className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="flex h-9 w-9 items-center justify-center rounded-lg border border-white/10 bg-white/5">
                      <ServiceIcon name={s.icon} size={16} className="text-fuchsia-400" />
                    </div>
                    <div>
                      <div className="font-medium text-white">{s.title}</div>
                      <div className="text-xs text-white/50">{s.features?.length || 0} features</div>
                    </div>
                  </div>
                </td>
                <td className="p-4 text-white/70">{s.tagline}</td>
                <td className="p-4 text-white/50">{s.order}</td>
                <td className="p-4">
                  <div className="flex justify-end gap-2">
                    <button onClick={() => openEdit(s)} data-testid={`service-edit-${s.slug}`} className="rounded-lg border border-white/10 p-2 text-white/70 hover:text-white"><Pencil size={14} /></button>
                    <button onClick={() => remove(s.id)} data-testid={`service-delete-${s.slug}`} className="rounded-lg border border-white/10 p-2 text-white/70 hover:text-white"><Trash2 size={14} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="service-editor-modal">
          <div className="w-full max-w-2xl overflow-hidden rounded-2xl border border-white/10 bg-[#131B2F]">
            <div className="flex items-center justify-between border-b border-white/10 p-5">
              <h3 className="text-lg font-semibold text-white">{editing === "new" ? "New service" : "Edit service"}</h3>
              <button onClick={close} className="text-white/60 hover:text-white"><X size={18} /></button>
            </div>
            <div className="max-h-[70vh] overflow-y-auto p-6 scrollbar-thin">
              <div className="grid gap-5">
                <Field label="Title"><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="service-title-input" /></Field>
                <Field label="Tagline"><Input value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} /></Field>
                <Field label="Description"><Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
                <div className="grid gap-5 md:grid-cols-2">
                  <Field label="Icon">
                    <select value={form.icon} onChange={(e) => setForm({ ...form, icon: e.target.value })} className="w-full rounded-lg border border-white/10 bg-[#0B1020] px-4 py-2.5 text-sm text-white">
                      {iconNames.map((n) => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </Field>
                  <Field label="Order"><Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: parseInt(e.target.value || "0", 10) })} /></Field>
                </div>
                <Field label="Features (one per line)">
                  <Textarea rows={4} value={(form.features || []).join("\n")} onChange={(e) => setForm({ ...form, features: e.target.value.split("\n").map((x) => x.trim()).filter(Boolean) })} />
                </Field>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 border-t border-white/10 p-5">
              <button onClick={close} className="rounded-full border border-white/15 px-5 py-2 text-xs text-white/80 hover:border-white/30">Cancel</button>
              <button onClick={save} disabled={saving} data-testid="service-save-btn" className="group relative inline-flex items-center gap-2 rounded-full px-6 py-2.5 text-xs font-semibold text-white disabled:opacity-60">
                <span className="absolute inset-0 rounded-full gradient-bg" />
                <span className="relative">{saving ? "Saving…" : "Save service"}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
